// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import GroupedLinesHeader from "@gcg/App/Components/Cart/GroupedLinesHeading";
import Notify from "@gcg/App/Components/Notify";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable from "@insite/mobius/Clickable/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@seacoastusa/Overrides/Widgets/Cart/CartLineCardExpanded";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentCart: getCurrentCartState(state).value,
    settingsCollection: getSettingsCollection(state),
});

type Props = ReturnType<typeof mapStateToProps>;
export interface CartLinesStyles {
    wrapper?: InjectableCss;
    noCartLinesText?: TypographyProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    continueShoppingLink?: InjectableCss;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
}

export const cartLinesStyles: CartLinesStyles = {
    continueShoppingLink: {
        css: css`
            color: ${getColor("primary.main")};
            font-size: 15px;
            margin-bottom: 10px;
            &:hover,
            &:focus {
                text-decoration: underline;
            }
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-wrap: wrap;
            padding-bottom: 15px;
            padding-top: 10px;
            position: relative;
            @media print {
                display: none;
            }
        `,
    },
    header: {
        itemCountText: {
            size: "28px",
            weight: "normal",
            transform: "uppercase",
            css: css`
                margin-bottom: 0;
                width: 190px;
                line-height: 20px;

                @media (max-width: 800px) {
                    width: 100%;
                }
            `,
        },
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 20px;
                `,
            },
        },
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CustomCartLines: FC<Props> = ({
    currentCart,
    settingsCollection,
}) => {

    const cart = currentCart;
    const cartLines = cart?.cartLines || [];

    if (cartLines?.length === 0) {
        return (
            <StyledSection>
                <Clickable {...styles.continueShoppingLink} onClick={() => window.history.back()}>
                    {translate("Continue Shopping")}
                </Clickable>
                <Notify
                    messageType="warning"
                    body={
                        <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                            {siteMessage("Cart_NoOrderLines")}
                        </Typography>
                    }
                />
            </StyledSection>
        );
    }

    const { productSettings } = settingsCollection;

    // shared functionality for grouped cart lines
    const getGroupedLines = (
        line: CartLineModel
    ) => {
        return (
            <CartLineContext.Provider
                value={line}
                key={`${line.id}`}
            >
                <CartLineCardExpanded
                    cart={cart!}
                    productSettings={productSettings}
                    extendedStyles={styles.cardExpanded}
                    showRemoveAction={true}
                    showCutWire={true}
                    editable={true}
                    productId={line?.productId}
                    dontShowCertification={true}
                    isSeacoastCustom={true}
                />
            </CartLineContext.Provider>
        );
    };

    const defaultView = cartLines?.map((line: any) => {
        return getGroupedLines(line);
    });

    return (
        <StyledSection {...styles.wrapper}>
            <StyledSection {...styles.headerWrapper} id="cartHeader">
                <Typography as="h3" variant="h3" {...styles.header?.itemCountText}>
                    {translate("Products")}
                </Typography>
            </StyledSection>

            {cartLines.length > 0 && (
                <StyledWrapper
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <GroupedLinesHeader {...({ isSeacoastCustom: true } as any)} />
                    <StyledWrapper>{defaultView}</StyledWrapper>
                </StyledWrapper>
            )}
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomCartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CustomCartPage"],
    },
};

export default widgetModule;